/** @format */

import { useEffect, useRef, useState } from "react";
import type { ImportAiQuestionState, ImportJob } from "@question-bank/shared";
import { useTranslation } from "react-i18next";
import {
	arrivingQuestionNumbers,
	flattenQuestionGaps,
	groupStageQuestions,
	importStageVisual,
	questionCrossesConnector,
	withQuestionGaps,
	type ImportStageStep,
	type ImportStageStepState,
	type StageQuestionNode,
} from "./importStageProgress";

const STATE_BADGE: Record<ImportStageStepState, string> = {
	waiting: "bg-slate-100 text-slate-700",
	inProgress: "bg-blue-100 text-blue-800",
	done: "bg-green-100 text-green-800",
	failed: "bg-red-100 text-red-800",
};

const RAIL: Record<ImportStageStepState, string> = {
	waiting: "border-slate-300 bg-white text-slate-400",
	inProgress: "border-blue-600 bg-blue-600 text-white motion-safe:animate-pulse",
	done: "border-green-600 bg-green-600 text-white",
	failed: "border-red-600 bg-red-600 text-white",
};

const CONNECTOR: Record<ImportStageStepState, string> = {
	waiting: "bg-slate-300",
	inProgress: "bg-slate-300",
	done: "bg-green-600",
	failed: "bg-slate-300",
};

const CARD: Record<ImportStageStepState, string> = {
	waiting: "border-slate-200 border-l-slate-300 bg-white",
	inProgress: "border-blue-200 border-l-blue-600 bg-blue-50",
	done: "border-slate-200 border-l-green-600 bg-white",
	failed: "border-red-200 border-l-red-600 bg-red-50",
};

const MARK: Record<ImportStageStepState, string> = {
	waiting: "",
	inProgress: "●",
	done: "✓",
	failed: "!",
};

const AI_CHIP: Record<ImportAiQuestionState, string> = {
	pending: "bg-amber-100 text-amber-900",
	complete: "bg-green-100 text-green-800",
	failed: "bg-red-100 text-red-800",
};

/** Sparkle beside Classify. The name says AI. The picture does not. */
const ClassifyAiIcon = ({ label }: { label: string }) => (
	<span role="img" aria-label={label} className="inline-flex text-blue-700">
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="size-4" aria-hidden="true">
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.5"
				d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"
			/>
		</svg>
	</span>
);

const QuestionChip = ({
	node,
	isQuestion,
	activeNumber,
	pinnedNumber,
	hasActiveQuestion,
	isArriving,
	onPreview,
	onPin,
	stateText,
	isFullNumber = false,
}: {
	node: StageQuestionNode;
	isQuestion: boolean;
	activeNumber: string | null;
	pinnedNumber: string | null;
	hasActiveQuestion: boolean;
	isArriving: boolean;
	onPreview: (number: string | null) => void;
	onPin: (number: string) => void;
	stateText: string | null;
	isFullNumber?: boolean;
}) => {
	const { t } = useTranslation();
	if (node.isGap) {
		return (
			<span
				role="img"
				aria-label={`${t("importStages.gap")} ${node.number}`}
				className="rounded border border-dashed border-amber-700 bg-amber-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-amber-950"
			>
				{isFullNumber ? node.number : node.label}
			</span>
		);
	}
	const chip = node.chip;
	if (!chip) {
		return <span className="rounded px-1.5 py-0.5 font-mono text-xs font-semibold text-slate-900">{node.label}</span>;
	}
	const isActive = activeNumber === chip.number;
	const isPinned = pinnedNumber === chip.number;
	const accessibleName = stateText ? `${chip.number}, ${stateText}` : chip.number;
	return (
		<button
			type="button"
			className={[
				"cursor-pointer rounded border-0 px-1.5 py-0.5 font-mono text-xs",
				"focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-700",
				chip.aiState
					? AI_CHIP[chip.aiState]
					: isQuestion
						? "bg-emerald-100 font-semibold text-emerald-950"
						: "bg-emerald-50 text-emerald-900",
				isActive ? "outline outline-2 outline-offset-1 outline-blue-700" : "",
				hasActiveQuestion && !isActive ? "opacity-40" : "",
				isArriving ? "motion-safe:animate-stage-question-in" : "",
			]
				.filter((className) => className !== "")
				.join(" ")}
			aria-label={accessibleName}
			aria-pressed={isPinned}
			title={t("importStages.showOnStages")}
			data-active={hasActiveQuestion ? String(isActive) : undefined}
			data-arriving={isArriving ? "true" : undefined}
			onMouseEnter={() => onPreview(chip.number)}
			onMouseLeave={() => onPreview(null)}
			onFocus={() => onPreview(chip.number)}
			onBlur={() => onPreview(null)}
			onClick={() => onPin(chip.number)}
		>
			<span aria-hidden="true">{node.label}</span>
			{stateText && <span className="sr-only">{`, ${stateText}`}</span>}
		</button>
	);
};

const QuestionBranch = ({
	node,
	depth,
	activeNumber,
	pinnedNumber,
	hasActiveQuestion,
	arrived,
	onPreview,
	onPin,
	stateTextFor,
}: {
	node: StageQuestionNode;
	depth: number;
	activeNumber: string | null;
	pinnedNumber: string | null;
	hasActiveQuestion: boolean;
	arrived: ReadonlySet<string>;
	onPreview: (number: string | null) => void;
	onPin: (number: string) => void;
	stateTextFor: (node: StageQuestionNode) => string | null;
}) => {
	const isQuestion = depth === 0;
	return (
		<li
			className={
				node.isGap
					? "flex max-w-full flex-wrap items-center gap-1 rounded border border-dashed border-amber-600 bg-amber-50 px-1 py-0.5"
					: isQuestion
						? "flex max-w-full flex-wrap items-center gap-1 rounded bg-slate-50 px-1 py-0.5"
						: "flex flex-wrap items-center gap-1"
			}
		>
			<QuestionChip
				node={node}
				isQuestion={isQuestion}
				activeNumber={activeNumber}
				pinnedNumber={pinnedNumber}
				hasActiveQuestion={hasActiveQuestion}
				isArriving={node.chip ? arrived.has(node.chip.number) : false}
				onPreview={onPreview}
				onPin={onPin}
				stateText={stateTextFor(node)}
			/>
			{node.children.length > 0 && (
				<ul
					className={
						isQuestion
							? "flex min-w-0 max-w-full flex-wrap items-center gap-1"
							: "flex flex-wrap items-center gap-1 border-l border-slate-300 pl-2"
					}
				>
					{node.children.map((child) => (
						<QuestionBranch
							key={child.number}
							node={child}
							depth={depth + 1}
							activeNumber={activeNumber}
							pinnedNumber={pinnedNumber}
							hasActiveQuestion={hasActiveQuestion}
							arrived={arrived}
							onPreview={onPreview}
							onPin={onPin}
							stateTextFor={stateTextFor}
						/>
					))}
				</ul>
			)}
		</li>
	);
};

const isStageOpenByDefault = (state: ImportStageStepState): boolean => state === "inProgress" || state === "failed";

const hasCollapsibleQuestions = (step: ImportStageStep): boolean =>
	(step.trackedQuestions ?? step.questionNumbers).length > 0;

const StageTitle = ({
	step,
	isOpen,
	hasQuestions,
}: {
	step: ImportStageStep;
	isOpen: boolean;
	hasQuestions: boolean;
}) => {
	const { t } = useTranslation();
	return (
		<>
			<span className="inline-flex items-center gap-1.5 font-semibold text-slate-800">
				{hasQuestions && <span aria-hidden="true">{isOpen ? "▾" : "▸"}</span>}
				<span>{t(`importStages.stages.${step.stage}`)}</span>
				{(step.stage === "ExtractAssets" || step.stage === "Classify" || step.stage === "ExtractKatex") && (
					<ClassifyAiIcon label={t("importStages.ai")} />
				)}
			</span>
			<span className="inline-flex items-center gap-2">
				{hasQuestions && <span className="sr-only">{isOpen ? t("importStages.hide") : t("importStages.show")}</span>}
				<span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${STATE_BADGE[step.state]}`}>
					{t(`importStages.state.${step.state}`)}
				</span>
			</span>
		</>
	);
};

const QuestionProgress = ({
	step,
	panelId,
	isOpen,
	activeNumber,
	pinnedNumber,
	onPreview,
	onPin,
}: {
	step: ImportStageStep;
	panelId: string;
	isOpen: boolean;
	activeNumber: string | null;
	pinnedNumber: string | null;
	onPreview: (number: string | null) => void;
	onPin: (number: string) => void;
}) => {
	const { t } = useTranslation();
	const tracked = step.trackedQuestions;
	const done = tracked
		? tracked.filter((question) => question.aiState !== "pending").length
		: step.questionNumbers.length;
	const total = tracked ? tracked.length : step.questionTotal;
	const chips = step.trackedQuestions ?? step.questionNumbers.map((number) => ({ number, aiState: undefined }));
	const numberKey = chips.map((chip) => chip.number).join("\n");
	const flowRef = useRef({ seen: new Set<string>(), hasSeeded: false });
	const arrivedRef = useRef(new Set<string>());
	const listed = numberKey === "" ? [] : numberKey.split("\n");
	const flow = arrivingQuestionNumbers(flowRef.current.seen, listed, flowRef.current.hasSeeded);
	for (const number of flow.arriving) {
		arrivedRef.current.add(number);
	}
	useEffect(() => {
		const nextListed = numberKey === "" ? [] : numberKey.split("\n");
		const next = arrivingQuestionNumbers(flowRef.current.seen, nextListed, flowRef.current.hasSeeded);
		flowRef.current = { seen: next.seen, hasSeeded: true };
	}, [numberKey]);
	if (done === 0 && total === undefined) {
		return null;
	}
	const countWord = step.stage === "ExtractMarkScheme" ? t("importStages.items") : t("importStages.questions");
	const summary =
		total !== undefined && done < total
			? `${done} ${t("importStages.of")} ${total} ${countWord}`
			: `${done > 0 ? done : total} ${countWord}`;
	const hasActiveQuestion = activeNumber !== null;
	const questions = withQuestionGaps(groupStageQuestions(chips));
	const gaps = flattenQuestionGaps(questions);
	const stateTextFor = (node: StageQuestionNode): string | null =>
		node.chip?.aiState ? t(`importStages.aiState.${node.chip.aiState}`) : null;
	const gapSummary = gaps.length > 0 ? `, ${gaps.length} ${t("importStages.gaps")}` : "";
	return (
		<div className="mt-2">
			<p className="text-xs font-semibold text-slate-600">
				{summary}
				{gapSummary}
			</p>
			{!isOpen && gaps.length > 0 && (
				<ul className="mt-1 flex flex-wrap gap-1" aria-label={t("importStages.gaps")}>
					{gaps.map((gap) => (
						<li key={gap.number}>
							<QuestionChip
								node={gap}
								isQuestion
								activeNumber={activeNumber}
								pinnedNumber={pinnedNumber}
								hasActiveQuestion={hasActiveQuestion}
								isArriving={false}
								onPreview={onPreview}
								onPin={onPin}
								stateText={null}
								isFullNumber
							/>
						</li>
					))}
				</ul>
			)}
			{questions.length > 0 && (
				<ul
					id={panelId}
					hidden={!isOpen}
					className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1"
					aria-label={countWord}
				>
					{questions.map((question) => (
						<QuestionBranch
							key={question.number}
							node={question}
							depth={0}
							activeNumber={activeNumber}
							pinnedNumber={pinnedNumber}
							hasActiveQuestion={hasActiveQuestion}
							arrived={arrivedRef.current}
							onPreview={onPreview}
							onPin={onPin}
							stateTextFor={stateTextFor}
						/>
					))}
				</ul>
			)}
		</div>
	);
};

/** PDF pipeline as a vertical flow: one state card per stage, joined by a connector. */
export const ImportStageStepper = ({ job }: { job: ImportJob }) => {
	const { t } = useTranslation();
	const visual = importStageVisual(job);
	const [pinnedNumber, setPinnedNumber] = useState<string | null>(null);
	const [previewNumber, setPreviewNumber] = useState<string | null>(null);
	const [openOverride, setOpenOverride] = useState<Partial<Record<string, boolean>>>({});
	const activeNumber = previewNumber ?? pinnedNumber;
	const pinQuestion = (number: string) => {
		setPinnedNumber((current) => (current === number ? null : number));
	};
	const toggleStage = (stage: string, isDefaultOpen: boolean) => {
		setOpenOverride((current) => ({ ...current, [stage]: !(current[stage] ?? isDefaultOpen) }));
	};

	if (visual.kind === "hidden") {
		return null;
	}

	if (visual.kind === "unrecorded") {
		return <p className="text-sm text-slate-600">{t("importStages.notRecorded")}</p>;
	}

	return (
		<section aria-labelledby="import-stages-heading">
			<h3 id="import-stages-heading" className="text-base font-semibold text-slate-800">
				{t("importStages.heading")}
			</h3>
			<ol className="mt-4 list-none p-0">
				{visual.steps.map((step, index) => {
					const isLast = index === visual.steps.length - 1;
					const hasQuestionFlow = questionCrossesConnector(visual.steps, index, activeNumber);
					const isDefaultOpen = isStageOpenByDefault(step.state);
					const isOpen = openOverride[step.stage] ?? isDefaultOpen;
					const hasQuestions = hasCollapsibleQuestions(step);
					const panelId = `import-stage-${step.stage}`;
					return (
						<li
							key={step.stage}
							aria-current={step.state === "inProgress" ? "step" : undefined}
							className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3"
						>
							<div className="flex flex-col items-center">
								<span
									aria-hidden="true"
									className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold ${RAIL[step.state]}`}
								>
									{MARK[step.state]}
								</span>
								{!isLast && (
									<span
										aria-hidden="true"
										data-question-flow={hasQuestionFlow ? "on" : "off"}
										className={`mt-1 w-0.5 min-h-4 flex-1 ${hasQuestionFlow ? "bg-blue-600" : CONNECTOR[step.state]}`}
									/>
								)}
							</div>
							<div
								className={`mb-3 rounded-md border border-l-4 px-4 py-3 shadow-(--admin-shadow-raised) ${CARD[step.state]}`}
							>
								{hasQuestions ? (
									<button
										type="button"
										className="flex w-full cursor-pointer flex-wrap items-center justify-between gap-2 border-0 bg-transparent p-0 text-left"
										aria-expanded={isOpen}
										aria-controls={panelId}
										onClick={() => toggleStage(step.stage, isDefaultOpen)}
									>
										<StageTitle step={step} isOpen={isOpen} hasQuestions />
									</button>
								) : (
									<div className="flex w-full flex-wrap items-center justify-between gap-2">
										<StageTitle step={step} isOpen={isOpen} hasQuestions={false} />
									</div>
								)}
								<QuestionProgress
									step={step}
									panelId={panelId}
									isOpen={isOpen}
									activeNumber={activeNumber}
									pinnedNumber={pinnedNumber}
									onPreview={setPreviewNumber}
									onPin={pinQuestion}
								/>
								{step.hasNoKatex && (
									<p className="mt-2 text-xs font-semibold text-slate-600">{t("importStages.noKatex")}</p>
								)}
							</div>
						</li>
					);
				})}
			</ol>
		</section>
	);
};
